<template>
  <header class="app-header" :class="[
      {'app-header--active': showMobileMenu},
      `app-header--${headerTheme}`
    ]">
    <!-- Logo部分（PC和Mobile共用） -->
    <div class="app-header__logo" @click="goToHome">
      <img :src="domainConfig?.['siteIcon']" class="app-header__logo-icon" alt="">
      <div class="app-header__logo-text">
        {{domainConfig?.['siteName']}}
      </div>
    </div>

    <!-- PC导航菜单 (只在桌面设备显示) -->
    <nav class="app-header__nav" v-if="deviceType === 'desktop' || deviceType === 'pc'">
      <div class="app-header__nav-item"
        :class="{'app-header__nav-item--active': isRouteActive('/')}" @click="goToHome">
        Home
      </div>
      <div class="app-header__nav-item app-header__nav-item--has-dropdown"
        :class="{'app-header__nav-item--active': isRouteActive('/all')}"
        @click="toggleCategoriesDropdown">
        Categories

        <svg class="app-header__nav-arrow"
          :class="{'app-header__nav-arrow--flip': showCategoriesDropdown}"
          xmlns="http://www.w3.org/2000/svg" width="6" height="4" viewBox="0 0 6 4" fill="none">
          <path
            d="M0.10709 0.750668L2.66661 3.84125C2.70787 3.89104 2.75905 3.93101 2.81663 3.95841C2.87422 3.98581 2.93686 4 3.00024 4C3.06363 4 3.12626 3.98581 3.18385 3.95841C3.24144 3.93101 3.29261 3.89104 3.33387 3.84125L5.89339 0.750668C6.13766 0.455668 5.93514 0 5.55976 0H0.439985C0.0646082 0 -0.137912 0.455668 0.10709 0.750668Z"
            fill="#2C3337" />
        </svg>

        <!-- Categories下拉菜单 -->
        <div class="app-header__dropdown" v-if="showCategoriesDropdown">
          <div class="app-header__dropdown-content">
            <div class="app-header__dropdown-items">
              <div class="app-header__dropdown-item" v-for="category in categories"
                :key="category.value" @click.stop="goToCategory(category.value)">
                <div class="app-header__dropdown-item-icon-container">
                  <img :src="category.originalIcon" alt="" class="app-header__dropdown-item-icon">
                </div>
                <div class="app-header__dropdown-item-text">{{ category.name }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="app-header__nav-item"
        :class="{'app-header__nav-item--active': isRouteActive('/rank')}"
        @click="$router.push('/rank')">
        Rankings
      </div>
      <div class="app-header__nav-item"
        :class="{'app-header__nav-item--active': isRouteActive('/about-us')}"
        @click="$router.push('/about-us')">
        About us
      </div>
    </nav>

    <!-- Mobile菜单按钮 (只在移动设备显示) -->
    <div class="app-header__mobile-trigger" v-if="deviceType === 'mobile'"
      @click="toggleMobileMenu">
      <img v-if="!showMobileMenu" :src="menuSrc" alt="">
      <img v-else src="@/assets/clos-menu.svg" alt="">
    </div>

    <!-- Mobile菜单 (显示/隐藏取决于showMobileMenu) -->
    <div class="app-header__mobile-menu" v-if="showMobileMenu">
      <div class="app-header__mobile-menu-items">
        <router-link v-for="(item, index) in navItems" :key="index" :to="getNavLink(item.link)"
          class="app-header__mobile-menu-item" :class="[
            {'app-header__mobile-menu-item--active': isRouteActive(item.link)},
            `app-header__mobile-menu-item--${headerTheme}`
          ]" @click.native="showMobileMenu = false">
          {{ item.name }}
          <svg xmlns="http://www.w3.org/2000/svg" width="7" height="10" viewBox="0 0 7 10"
            fill="none">
            <path d="M1.54175 1.5L5.04175 5L1.54175 8.5" stroke="#050B20" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </router-link>
      </div>
    </div>
  </header>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'AppHeader',
  props: {
    headerTheme: {
      type: String,
      default: 'light' // 可选值：'light'或'dark'
    },
  },
  computed: {
    ...mapGetters({
      deviceType: 'deviceType',
      domainConfig: 'domainConfig',
      categories: 'categories',
      categoriesLoaded: 'categoriesLoaded'
    }),
    menuSrc () {
      return require('@/assets/mobile-menu.svg')
    }
  },
  data () {
    return {
      showMobileMenu: false,
      showCategoriesDropdown: false,
      navItems: [
        { name: 'Home', link: '/' },
        { name: 'Categories', link: '/all' },
        { name: 'Rankings', link: '/rank' },
        { name: 'About us', link: '/about-us' },
      ]
    };
  },
  methods: {
    toggleMobileMenu () {
      this.showMobileMenu = !this.showMobileMenu;

      // 如果打开菜单，禁止body滚动
      if (this.showMobileMenu) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    },

    toggleCategoriesDropdown () {
      this.showCategoriesDropdown = !this.showCategoriesDropdown;
    },

    // 判断当前路由是否激活
    isRouteActive (link) {
      // 获取不包含channel参数的当前路径
      let currentPath = this.$route.path;
      const channel = this.$route.params.channel;

      // 如果存在channel参数，移除channel部分以便正确比较
      if (channel && currentPath.startsWith(`/${channel}`)) {
        currentPath = currentPath.replace(`/${channel}`, '') || '/';
      }

      // 处理根路径特殊情况
      if (link === '/' && (currentPath === '/' || currentPath === '')) {
        return true;
      }

      // 处理首页特殊情况
      if (link === '/' && currentPath !== '/') {
        return false;
      }

      // Categories页面特殊处理
      if (link === '/categories') {
        // 如果路径是/categories或者是带有特定类别的路径（如/biography, /romance等）
        if (currentPath === '/categories') {
          return true;
        }

        // 检查是否是类别页面
        const categoryValues = this.categories.map(c => `/${c.value}`);
        if (categoryValues.includes(currentPath)) {
          return true;
        }

        return false;
      }

      // 其他页面直接比较路径
      return currentPath === link;
    },

    goToHome () {
      if (this.$route.path !== '/') {
        // 检查是否存在channel参数
        if (this.$route.params.channel) {
          // 如果有channel参数，使用完整路径
          window.location.href = `/${this.$route.params.channel}`;
        } else {
          // 保留查询参数
          this.$router.push({
            path: '/',
          });
        }
      }
    },

    // 获取导航链接
    getNavLink (link) {
      // 获取当前URL的查询参数
      const currentQuery = this.$route.query;
      const queryString = Object.keys(currentQuery).length > 0
        ? `?${new URLSearchParams(currentQuery).toString()}`
        : '';

      // 检查是否存在channel参数
      if (this.$route.params.channel) {
        // 如果是首页链接
        if (link === '/') {
          return `/${this.$route.params.channel}/${queryString}`;
        }
        // 其他链接
        return `/${this.$route.params.channel}${link}${queryString}`;
      }
      return `${link}${queryString}`;
    },

    // 导航到特定类别页面
    goToCategory (category) {
      // 关闭下拉菜单
      this.showCategoriesDropdown = false;

      // 检查是否存在channel参数
      if (this.$route.params.channel) {
        // 如果有channel参数，使用完整路径
        this.$router.push(`/${this.$route.params.channel}/${category}`);
      } else {
        // 如果没有channel参数，直接导航到类别页面
        this.$router.push(`/${category}`);
      }
    },
  },
  beforeDestroy () {
    // 确保在组件销毁时恢复body滚动
    document.body.style.overflow = '';
  },
  mounted () {
    // 监听路由变化，关闭移动菜单
    this.$router.afterEach(() => {
      if (this.showMobileMenu) {
        this.showMobileMenu = false;
        document.body.style.overflow = '';
      }

      // 关闭分类下拉菜单
      this.showCategoriesDropdown = false;
    });

    // 点击其他区域关闭下拉菜单
    document.addEventListener('click', (event) => {
      const dropdown = document.querySelector('.app-header__nav-item--has-dropdown');
      if (dropdown && !dropdown.contains(event.target) && this.showCategoriesDropdown) {
        this.showCategoriesDropdown = false;
      }
    });
  }
};
</script>

<style lang="scss">
@import '~@/css/variables.scss';
</style>
